import { useCallback, useEffect, useRef, useState } from "react";

import { getFirebaseAssetURL } from "utils/getFirebaseAssetURL";

interface TResolved {
  path: string;
  /** `null` = resolve skončil, ale URL z něj nevzešla (chybějící soubor ve Storage). */
  url: string | null;
}

// Obálka se začne resolvovat o kus dřív, než dlaždice vjede do viewportu, aby při běžném
// scrollování stihla naskočit dřív, než na ni student dokouká. Sto procent výšky viewportu
// je zhruba jedna „obrazovka dopředu" — víc už by u 567 dlaždic začalo mazat úsporu.
const OBSERVER_MARGIN = "100% 0px";

/**
 * Přeloží cestu ve Firebase Storage na stahovatelnou URL, ale teprve až je element na obrazovce.
 *
 * AIDEV-NOTE: Existuje vedle `useAssetUrlResolver`, ne místo něj. Ten hook má přes dvacet
 * volajících (SelfEvaluation, SolutionModal, AssignmentPrompt, pracovní list ve zkoušení) a
 * všude jde o JEDEN obrázek na obrazovce, kde je odklad jen zdržení. Tenhle je pro mřížky:
 * `/knihy/vsechny` renderuje 567 dlaždic bez virtualizace a každá si dnes volá
 * `getDownloadURL()` hned při mountu — tedy 567 requestů na Storage API v jedné vlně, které
 * soutěží o spojení se samotným GraphQL dotazem. [TD-362]
 *
 * Vrací `ref` na element, jehož viditelnost se sleduje — u dlaždice je to obal obálky, ne
 * `<img>`: ten se vykreslí až když URL existuje, takže by nebylo co pozorovat.
 */
export const useVisibleAssetUrl = (assetPath: string | null | undefined) => {
  // Cesta se drží u URL: po přepnutí knihy tak ani jediný render nevrátí URL té předchozí.
  // Týž vzor jako `useAssetUrlResolver` a dvojice `{ path, url }` v `useBookPhotos`.
  const [resolved, setResolved] = useState<TResolved | null>(null);

  // Kde IntersectionObserver není (jsdom, starší prohlížeče), chová se hook jako dosud a
  // resolvuje rovnou — degradace do dnešního stavu, ne do prázdné obálky.
  // Stejný fallback jako ExamSelfEvaluation.tsx:104. [TD-312]
  const [isVisible, setIsVisible] = useState(typeof IntersectionObserver === "undefined");

  const observerRef = useRef<IntersectionObserver | null>(null);

  // AIDEV-NOTE: Observer se zakládá v ref callbacku, ne v efektu. Efekt běží až po commitu a
  // četl by `ref.current`, jenže u dlaždice pod `content-visibility: auto` se pořadí commitů
  // a protnutí nedá spolehlivě předpokládat — ref callback dostane element v okamžiku, kdy
  // vznikne. Je to týž důvod, proč `useImageLoaded` čte `img.complete` v refu. [TD-362]
  const ref = useCallback((node: Element | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (node === null) return;

    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;

        setIsVisible(true);
        // Dlaždice se při scrollování vrací do viewportu opakovaně; bez odpojení by každý
        // návrat byl další resolve.
        observer.disconnect();
      },
      { rootMargin: OBSERVER_MARGIN },
    );

    observer.observe(node);
    observerRef.current = observer;
  }, []);

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    },
    [],
  );

  useEffect(() => {
    if (!isVisible) return undefined;
    if (typeof assetPath !== "string" || assetPath.length === 0) return undefined;

    // Kniha se mezitím může vyměnit (jiné id v URL) — pozdní odpověď nesmí přepsat novější.
    let isCurrent = true;

    getFirebaseAssetURL(assetPath)
      .then(url => {
        if (!isCurrent) return;
        // AIDEV-NOTE: I neúspěch se zapisuje, jen s `url: null`. `getFirebaseAssetURL` chybu
        // spolkne a vrátí `undefined`; kdyby se to tu zahodilo, volající by nikdy nepoznal
        // „ještě se stahuje" od „nestáhne se nikdy" a ghost by u rozbité cesty svítil
        // navždy. `url` z hooku zůstává `null` v obou případech, takže se pro volající,
        // kteří `isSettled` nečtou (BookTile, BookCard), nemění nic. [TD-363]
        setResolved({ path: assetPath, url: url ?? null });
      })
      .catch(console.error);

    return () => {
      isCurrent = false;
    };
  }, [assetPath, isVisible]);

  const isSettled = resolved !== null && resolved.path === assetPath;

  return { url: isSettled ? resolved.url : null, ref, isVisible, isSettled };
};
